/**
 * AuthMediaThumb — auth-aware image renderer for /api/media/{id}
 *
 * Pending/unapproved media (`is_published=false` or `moderation_status≠approved`)
 * requires an `Authorization: Bearer …` header on every GET. A plain `<img src>`
 * tag cannot send custom headers, so the browser renders a grey placeholder.
 *
 * Stability notes (BA-002C):
 *
 *   1. We use native `fetch()` rather than the shared axios client. CRA's
 *      `react-error-overlay` wraps XMLHttpRequest and, on a non-2xx blob
 *      response, accesses `xhr.responseText` to format an error message —
 *      that access throws a DOMException ("The value is only accessible if
 *      the object's 'responseType' is '' or 'text'"), which then surfaces
 *      via window.onerror as the cross-origin "Script error." runtime
 *      overlay the owner observed. Native fetch() does not trigger the
 *      sniffer and never accesses responseText.
 *
 *   2. The fetch is bound to an AbortController; on unmount we abort the
 *      in-flight request before the response is processed. That kills the
 *      promise chain cleanly and prevents post-unmount setState attempts.
 *
 *   3. The active blob URL lives in a useRef. Cleanup defers
 *      `URL.revokeObjectURL` via `setTimeout(0)` so any in-flight `<img>`
 *      render that already used this URL can complete before it becomes
 *      invalid (React 18 strict-mode + refetch race protection).
 *
 *   4. The `<img>` has a local `onError` handler that flips into the
 *      `image unavailable` state without bubbling to `window.onerror`.
 *
 *   5. Variant fallback chain `thumbnail → preview → original` handles
 *      legacy uploads or partial-variant rows.
 *
 * Public safety: this component does NOT widen any read path. The backend
 * still enforces `is_published AND moderation_status=='approved'` for
 * anonymous reads; only authenticated owners/admins can view pending media,
 * and that's exactly the audience that mounts this component.
 */
import { useEffect, useRef, useState } from 'react';

const DEFAULT_FALLBACK_CHAIN = ['thumbnail', 'preview', 'original'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const _readToken = () =>
  (typeof window !== 'undefined' &&
    (window.localStorage?.getItem('treatx_unified_token') ||
      window.localStorage?.getItem('treatx_patient_token'))) ||
  '';

// PRODUCTION TRUTH CORRECTION (2026-08-21) — F1/F2/F3 root cause.
//
// Post FIND-H-08 the JWT lives in an HttpOnly `treatx_auth` cookie —
// `treatx_unified_token` / `treatx_patient_token` are NO LONGER
// populated, so `_readToken()` returns `''`. Combined with
// `credentials: 'omit'` below, every authenticated media fetch went
// out with NO auth transport whatsoever → backend 401 → "image
// unavailable" on Provider Admin logo/facility surfaces AND TreatX
// Admin moderation. Patient Search kept working because approved
// logos are `is_public=true` and served without auth.
//
// The backend was already extended in the prior batch to accept the
// cookie AS an alternative JWT transport; the missing half was the FE
// component refusing to send that cookie. `credentials: 'include'`
// tells the browser to attach the same-origin `treatx_auth` cookie.
// Header transport is retained as a legacy fallback for any old
// bearer that MAY still exist in localStorage.

const AuthMediaThumb = ({
  mediaId,
  alt,
  variants,
  url,           // TREATX-L1E: optional full API path (overrides /api/media/{id}). Used for token-scoped patient consent reads.
  className = 'w-full h-full object-cover',
  testIdPrefix = 'thumb',
}) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const blobUrlRef = useRef(null);
  const chainRef = useRef(null);
  if (chainRef.current === null) {
    chainRef.current = variants && variants.length > 0 ? variants : DEFAULT_FALLBACK_CHAIN;
  }

  useEffect(() => {
    if (!mediaId) {
      setFailed(true);
      return undefined;
    }
    setSrc(null);
    setFailed(false);

    const controller = new AbortController();
    let cancelled = false;

    const tryNext = async (idx) => {
      if (cancelled) return;
      if (idx >= chainRef.current.length) {
        if (!cancelled) setFailed(true);
        return;
      }
      const variant = chainRef.current[idx];
      // TREATX-L1E: allow caller to supply a fully-qualified API path
      // (e.g. token-authorised patient consent reader). When `url` is
      // provided, we STRIP any existing `variant=` param the caller
      // may have baked into it before appending the current chain
      // variant — otherwise FastAPI would receive two values and pick
      // the wrong one.
      let finalUrl;
      if (url) {
        const stripped = url.replace(/([?&])variant=[^&]*(&|$)/i, (_, l, r) => (r ? l : ''))
                            .replace(/[?&]$/, '');
        finalUrl = `${BACKEND_URL}${stripped}${stripped.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variant)}`;
      } else {
        finalUrl = `${BACKEND_URL}/api/media/${encodeURIComponent(mediaId)}?variant=${encodeURIComponent(variant)}`;
      }
      const token = _readToken();
      try {
        const resp = await fetch(finalUrl, {
          method: 'GET',
          // Attach same-origin `treatx_auth` HttpOnly cookie.
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        });
        if (cancelled) return;
        if (resp.ok) {
          const blob = await resp.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          blobUrlRef.current = objectUrl;
          setSrc(objectUrl);
          return;
        }
        // Variant-missing-style failures walk down the chain.
        if (resp.status === 404 || resp.status === 400) {
          await tryNext(idx + 1);
          return;
        }
        // Any other status (401, 403, 5xx, …) → graceful local failure.
        if (!cancelled) setFailed(true);
      } catch (err) {
        if (err && err.name === 'AbortError') return; // unmount, ignore
        // Local failure — never let this bubble to window.onerror.
        if (!cancelled) {
          // eslint-disable-next-line no-console
          if (typeof console !== 'undefined' && console.debug) {
            console.debug('[AuthMediaThumb] suppressed media fetch error', err);
          }
          setFailed(true);
        }
      }
    };
    tryNext(0);

    return () => {
      cancelled = true;
      try { controller.abort(); } catch { /* noop */ }
      const cleanupUrl = blobUrlRef.current;
      blobUrlRef.current = null;
      if (cleanupUrl) {
        setTimeout(() => {
          try { URL.revokeObjectURL(cleanupUrl); } catch { /* noop */ }
        }, 0);
      }
    };
  }, [mediaId, url]);

  if (failed) {
    return (
      <div
        className="w-full h-full flex items-center justify-center text-[10px] text-rose-500 bg-rose-50 border border-rose-200"
        data-testid={`${testIdPrefix}-failed-${mediaId}`}
        title={`media ${mediaId} could not be loaded`}
      >
        image unavailable
      </div>
    );
  }
  if (!src) {
    return (
      <div
        className="w-full h-full bg-slate-100 animate-pulse"
        data-testid={`${testIdPrefix}-loading-${mediaId}`}
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      data-testid={`${testIdPrefix}-img-${mediaId}`}
      onError={() => { setFailed(true); }}
    />
  );
};

export default AuthMediaThumb;
export { AuthMediaThumb };
